import React from "react"

import { ToolCallMessage } from "./ToolCallMessage"
import type { WordHighlight } from "./word-highlight"

const TOOL_CALL_STRINGS = [
  "function",
  "tool_call",
  "tool",
  "tool_name",
  "dtmf_tool",
]

interface MessageContentProps {
  content: unknown
  className?: string
  highlightedWords?: WordHighlight[] | null
}

const isToolCallContent = (content: unknown): boolean => {
  let object: object = {}
  if (typeof content === "object" && content !== null) {
    object = content
  }

  if (typeof content === "string") {
    try {
      const parsed: unknown = JSON.parse(content)
      if (typeof parsed === "object" && parsed !== null) {
        object = parsed
      }
    } catch {
      return (
        content.trim().startsWith("{tool_call:") &&
        content.includes("arguments:")
      )
    }
  }

  return (
    typeof object === "object" &&
    object !== null &&
    TOOL_CALL_STRINGS.some((key) => Object.hasOwn(object, key))
  )
}

const processMessageContent = (content: unknown): string | number | boolean => {
  if (typeof content === "object" && content !== null) {
    return JSON.stringify(content, null, 2)
  }
  return (content as string | number | boolean | null | undefined) || ""
}

export const MessageContent: React.FC<MessageContentProps> = ({
  content,
  className = "",
  highlightedWords = null,
}) => {
  // Check if this is a tool call and render it specially
  if (isToolCallContent(content)) {
    return <ToolCallMessage content={content} className={className} />
  }

  const displayContent = processMessageContent(content)

  // If no word highlights, render normally
  if (!highlightedWords || highlightedWords.length === 0) {
    return (
      <div
        dir="auto"
        className={`text-sm leading-relaxed break-words whitespace-pre-wrap ${className}`}
      >
        {displayContent}
      </div>
    )
  }

  // Render with word highlights
  const contentString = typeof displayContent === "string" ? displayContent : ""

  // Sort highlights by start offset
  const sortedHighlights = [...highlightedWords].sort(
    (a, b) => a.startOffset - b.startOffset,
  )

  // Merge overlapping highlights
  const mergedHighlights: WordHighlight[] = []
  let currentHighlight: WordHighlight | null = null

  sortedHighlights.forEach((highlight) => {
    if (!currentHighlight) {
      currentHighlight = { ...highlight }
    } else if (highlight.startOffset <= currentHighlight.endOffset) {
      // Overlapping or adjacent highlights - merge them
      currentHighlight.endOffset = Math.max(
        currentHighlight.endOffset,
        highlight.endOffset,
      )
    } else {
      // Non-overlapping highlight - save current and start new one
      mergedHighlights.push(currentHighlight)
      currentHighlight = { ...highlight }
    }
  })

  // Don't forget the last highlight
  if (currentHighlight) {
    mergedHighlights.push(currentHighlight)
  }

  // Render content with highlights
  const parts: React.ReactNode[] = []
  let lastIndex = 0

  mergedHighlights.forEach((highlight, idx) => {
    // Ensure offsets are within bounds
    const startOffset = Math.max(
      0,
      Math.min(highlight.startOffset, contentString.length),
    )
    const endOffset = Math.max(
      startOffset,
      Math.min(highlight.endOffset, contentString.length),
    )

    // Add text before highlight
    if (startOffset > lastIndex) {
      parts.push(
        <span key={`text-${idx}`}>
          {contentString.substring(lastIndex, startOffset)}
        </span>,
      )
    }

    // Handle different error types
    if (highlight.errorType === "insert") {
      const text = contentString.substring(startOffset, endOffset)
      const displayText = text || highlight.hypothesisError
      if (displayText) {
        parts.push(
          <span
            key={`highlight-${idx}`}
            className="bg-blue-300 dark:bg-blue-800 text-blue-900 dark:text-blue-100 px-1.5 py-0.5 underline rounded-sm"
          >
            {displayText}
          </span>,
        )
      }
      lastIndex = endOffset > startOffset ? endOffset : startOffset
    } else if (highlight.errorType === "delete") {
      // Show missing word in color (insert it visually)
      // For delete errors, startOffset === endOffset (zero-width), so we insert the word
      if (highlight.missingWord) {
        parts.push(
          <span
            key={`highlight-${idx}`}
            className="bg-blue-300 dark:bg-blue-800 text-blue-900 dark:text-blue-100 px-0.5 rounded-sm underline decoration-dotted"
            title={`Missing word: ${highlight.missingWord}`}
          >
            {highlight.missingWord}
          </span>,
        )
      }
      // For delete, advance to startOffset to continue rendering
      lastIndex = Math.max(lastIndex, startOffset)
    } else if (
      highlight.errorType === "substitute" ||
      highlight.errorType === "replace"
    ) {
      // Highlight hypothesis error (what was actually transcribed)
      // Backend sends "replace", frontend displays as "substitute"
      if (endOffset > startOffset) {
        parts.push(
          <span
            key={`highlight-${idx}`}
            className="bg-yellow-300 dark:bg-yellow-800 text-yellow-900 dark:text-yellow-100 px-0.5 rounded-sm"
            title={
              highlight.missingWord
                ? `Should be: ${highlight.missingWord}`
                : undefined
            }
          >
            {contentString.substring(startOffset, endOffset)}
          </span>,
        )
        lastIndex = endOffset
      } else {
        lastIndex = startOffset
      }
    } else if (endOffset > startOffset) {
      // Default: red highlight for unknown error types
      parts.push(
        <span
          key={`highlight-${idx}`}
          className="bg-red-300 dark:bg-red-800 text-black dark:text-white px-0.5 rounded-sm"
        >
          {contentString.substring(startOffset, endOffset)}
        </span>,
      )
      lastIndex = endOffset
    } else {
      lastIndex = startOffset
    }
  })

  // Add remaining text
  if (lastIndex < contentString.length) {
    parts.push(<span key="text-end">{contentString.substring(lastIndex)}</span>)
  }

  return (
    <div
      dir="auto"
      className={`text-sm leading-relaxed break-words whitespace-pre-wrap ${className}`}
    >
      {parts}
    </div>
  )
}
